import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import { getUsersAdminService } from "../../../services/adminServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { setMetaAction } from "../../../redux/actions/metaActions/metaActions";
import {
  assignTicketUserService,
  closeTicketUserService,
  createTicketUserService,
  deleteTicketUserService,
  getTicketUserService,
  getTicketsUserService,
  sendTicketMessageUserService,
} from "../../../services/userServices";
import { convertRoleToText } from "../../../utils/enum";

export const ticketUserContext = createContext({
  tickets: [],
  setTickets: () => {},
  ticket: {},
  setTicket: () => {},
  status: "",
  setStatus: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleGetUsers: () => {},
  handleCreateTicket: () => {},
  handleGetTickets: () => {},
  handleGetTicket: () => {},
  handleSendTicketMessage: () => {},
  handleCloseTicket: () => {},
  handleDeleteTicket: () => {},
  handleAssignTicket: () => {},
});

const TicketUserContext = ({ children }) => {
  const dispatch = useDispatch();

  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState({});
  const [status, setStatus] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
      pageNumber: 1,
      itemsCount: 200,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${
                item.phoneNumber
              } - ${convertRoleToText(item.role)}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Ticket
  const handleCreateTicket = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createTicketUserService(userData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTickets();
        setIsShowModal(false);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Tickets
  const handleGetTickets = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      status,
      filter,
      pageNumber,
      sort,
      itemsCount,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getTicketsUserService(objData);
      if (status === 200) {
        setTickets(data.result?.tickets);
        setPageCount(data?.result?.allPagesCount);
        dispatch(setMetaAction());
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Ticket
  const handleGetTicket = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getTicketUserService(id);
      if (status === 200) {
        dispatch(setMetaAction());
        setTicket(data.result?.ticket);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Send Ticket Message
  const handleSendTicketMessage = async (event) => {
    event?.preventDefault();

    const message = event.target.message.value;
    const file = event.target.clip.value;

    if (!message) return toastErrorMessage("متن پیام الزامی است");

    const objData = {
      id: ticket._id,
      message,
      file,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await sendTicketMessageUserService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTickets();
        handleGetTicket(ticket._id);
        event.target.message.value = "";
        event.target.clip.value = "";
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Close Ticket
  const handleCloseTicket = async () => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await closeTicketUserService(ticket._id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTickets();
        handleGetTicket(ticket._id);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Ticket
  const handleDeleteTicket = async () => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteTicketUserService(ticket._id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTickets();
        setTicket({});
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Assign Ticket
  const handleAssignTicket = async (userData) => {
    const objData = {
      id: ticket._id,
      assign: userData.assign?.map((_item) => _item.value),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await assignTicketUserService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetTicket(ticket._id);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <ticketUserContext.Provider
      value={{
        tickets,
        setTickets,
        ticket,
        setTicket,
        status,
        setStatus,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleGetUsers,
        handleCreateTicket,
        handleGetTickets,
        handleGetTicket,
        handleSendTicketMessage,
        handleCloseTicket,
        handleDeleteTicket,
        handleAssignTicket,
      }}
    >
      {children}
    </ticketUserContext.Provider>
  );
};

export default TicketUserContext;
